import { useCallback, useState } from 'react';

export interface PhoneVerifyResult {
  selfTicketUuid?: string;
  subdomain?: string;
}

export type PhoneVerifyStage = 'phone' | 'code';

const csrfToken = () =>
  (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? '';

const postJson = async (route: string, payload: Record<string, string>) => {
  const res = await fetch(route, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, body };
};

// Format 10 raw digits as "(415) 555-1234" for display only.
export const formatNANP = (digits: string) => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export const toE164 = (digits: string) => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  return d.length === 10 ? `+1${d}` : '';
};

// Where a trial user goes once their phone is verified: the A2P form, unless they told the
// survey they want a toll-free number, in which case A2P is skipped and upgrading comes next.
export const afterPhoneVerifiedPath = (subdomain: string, prefersTfn: boolean) => (
  prefersTfn ? `/accounts/${subdomain}/billing_v2/upgrade` : `/accounts/${subdomain}/tcr/first_submission`
);

interface Options {
  subdomain: string;
  onVerified: (result?: PhoneVerifyResult) => void;
}

// Two-stage trial phone verification (send a code, then confirm it) against
// /v1/phone_verifications. Shared by the inline onboarding card and the modals.
export const usePhoneVerification = ({ subdomain, onVerified }: Options) => {
  const [stage, setStage] = useState<PhoneVerifyStage>('phone');
  const [phoneDigits, setPhoneDigitsRaw] = useState('');
  const [code, setCodeRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneFormatted, setPhoneFormatted] = useState('');
  const phoneComplete = phoneDigits.length === 10;

  const setPhoneDigits = useCallback((value: string) => {
    setPhoneDigitsRaw(value.replace(/\D/g, '').slice(0, 10));
  }, []);
  const setCode = useCallback((value: string) => {
    setCodeRaw(value.replace(/\D/g, '').slice(0, 6));
  }, []);

  const sendCode = useCallback(async () => {
    if (submitting || !phoneComplete) return false;
    setSubmitting(true);
    setError(null);
    try {
      const { ok, body } = await postJson('/v1/phone_verifications', { subdomain, phone: toE164(phoneDigits) });
      if (!ok) {
        setError(body?.errors?.[0] ?? 'Could not send verification code. Try again.');
        return false;
      }
      setPhoneFormatted(body?.data?.phone_formatted ?? formatNANP(phoneDigits));
      setStage('code');
      return true;
    } catch {
      setError('Network error. Try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [phoneDigits, phoneComplete, subdomain, submitting]);

  const confirmCode = useCallback(async () => {
    if (submitting || code.length !== 6) return false;
    setSubmitting(true);
    setError(null);
    try {
      const { ok, body } = await postJson('/v1/phone_verifications/confirm', { subdomain, code });
      if (!ok) {
        setError(body?.errors?.[0] ?? 'Invalid or expired code.');
        return false;
      }
      // V1 controllers wrap success payloads under data (see Respondable).
      const selfTicket = body?.data?.self_ticket;
      onVerified(selfTicket ? { selfTicketUuid: selfTicket.uuid, subdomain: selfTicket.subdomain } : undefined);
      return true;
    } catch {
      setError('Network error. Try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [code, subdomain, submitting, onVerified]);

  const useDifferentNumber = useCallback(() => {
    setStage('phone');
    setCodeRaw('');
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setStage('phone');
    setPhoneDigitsRaw('');
    setCodeRaw('');
    setError(null);
    setSubmitting(false);
    setPhoneFormatted('');
  }, []);

  return {
    stage,
    phoneDigits,
    setPhoneDigits,
    phoneComplete,
    code,
    setCode,
    submitting,
    error,
    phoneFormatted,
    sendCode,
    confirmCode,
    useDifferentNumber,
    reset,
  };
};
